import ApiService from "@/core/services/api.service";

/**
 * Kullanıcının şirket (corporate) erişim bilgisi
 *
 * Bir kurumun altında birden fazla şirket olabilir.
 * Kullanıcı company_admin değilse ve bir şirkete atanmışsa:
 * - Sadece o şirketin verisini görmeli
 * - Şirket seçimini değiştirememeli
 *
 * Bu servis api/user/self sonucunu cache'ler; CorporateSelect ve liste
 * sayfaları aynı kaynağı paylaşır (localStorage kullanılmaz, güncel olmayabilir).
 *
 * Kullanım (liste sayfası, ilk veri çekilmeden önce):
 *   const corporateAccess = await getAssignedCorporateAccess();
 *   this.keepFilter = applyAssignedCorporateToFilter(this.keepFilter, corporateAccess);
 *   this.filterResult();
 *
 * Şirket seçim alanı için: <CorporateSelect :prefill-from-self="true" />
 * (seçimi ve kilitlemeyi kendi içinde halleder)
 */

let cachedCorporateAccess = null;
let pendingAccessRequest = null;

/** Kullanıcı kaydından atandığı şirketi ({id, name}) çıkarır; atanmamışsa null. */
function extractAssignedCorporate(user) {
  if (!user) return null;

  if (user.corporate?.id) {
    return {
      id: user.corporate.id,
      name: user.corporate.name,
    };
  }

  if (user.corporate_id) {
    return {
      id: user.corporate_id,
      name: user.corporate_name || user.corporate?.name || String(user.corporate_id),
    };
  }

  return null;
}

/** api/user/self cevabından erişim kararını üretir. */
function buildCorporateAccess(selfUser) {
  const roles = Array.isArray(selfUser?.roles) ? selfUser.roles : [];
  const isCompanyAdmin = roles.some((role) => role?.name === "company_admin");
  const assignedCorporate = extractAssignedCorporate(selfUser);

  // company_admin olmayan ve bir şirkete atanmış kullanıcı o şirkete kilitlenir
  const isLockedToAssignedCorporate = !isCompanyAdmin && !!assignedCorporate;

  return {
    isLockedToAssignedCorporate,
    isCompanyAdmin,
    assignedCorporate: isLockedToAssignedCorporate ? assignedCorporate : null,
  };
}

/**
 * Atanmış şirket erişim bilgisini döner (tek seferlik API, sonra cache).
 * @returns {Promise<{
 *   isLockedToAssignedCorporate: boolean,
 *   isCompanyAdmin: boolean,
 *   assignedCorporate: {id:number,name:string}|null
 * }>}
 */
export function getAssignedCorporateAccess() {
  if (cachedCorporateAccess) {
    return Promise.resolve(cachedCorporateAccess);
  }

  if (pendingAccessRequest) {
    return pendingAccessRequest;
  }

  pendingAccessRequest = ApiService.get("api/user/self")
    .then((response) => {
      cachedCorporateAccess = buildCorporateAccess(response?.data || null);
      return cachedCorporateAccess;
    })
    .catch(() => {
      // self alınamazsa kimseyi kilitleme; normal davranış devam etsin
      cachedCorporateAccess = {
        isLockedToAssignedCorporate: false,
        isCompanyAdmin: false,
        assignedCorporate: null,
      };
      return cachedCorporateAccess;
    })
    .finally(() => {
      pendingAccessRequest = null;
    });

  return pendingAccessRequest;
}

/**
 * Kilitli kullanıcı için filtre objesine atandığı şirketi ekler.
 * Kilitli değilse filtreyi olduğu gibi döner.
 */
export function applyAssignedCorporateToFilter(filter, corporateAccess) {
  if (!corporateAccess?.isLockedToAssignedCorporate) {
    return filter;
  }

  return {
    ...(filter || {}),
    corporate_id: [corporateAccess.assignedCorporate],
  };
}

/** Test / logout sonrası cache temizliği */
export function clearAssignedCorporateAccessCache() {
  cachedCorporateAccess = null;
  pendingAccessRequest = null;
}
